import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { InfoBox } from './InfoBox';
import {
    checkIsArray,
    checkIsNumber,
    checkString,
} from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.helpers';
import { CampaignModel } from 'src/domains/casino/state/AppCasino/store/campaign-store/CampaignModel';
import { CasinoGamesViewer } from './CasinoGamesViewer';
import { SportsViewer } from './SportsViewer';
interface ActionCondiionsPropsType {
    campaignModel: CampaignModel;
}

const BettingType = observer('BettingType', ({ campaignModel: { actionConditions } }: ActionCondiionsPropsType) => {
    if (actionConditions?.bettingPlatform !== 'sports-book') {
        return null;
    }

    const betTypes = actionConditions.criteria?.find((item) => item.type === 'bet-type');
    const minBetsOdds = actionConditions.criteria?.find((item) => item.type === 'bet-min-odds');

    const betTypesParsed = checkIsArray(betTypes?.value)?.reduce((prev, curr) => `${prev} ${curr},`, '');
    const minBetsOddsParsed = checkIsNumber(minBetsOdds?.value);

    return (
        <div>
            <h4 className='text-sm m-0 mb-2 font-semibold'>Betting Types & Odds Criteria</h4>
            <div className='mb-3'>
                <h5 className='m-0 text-xs text-muted-foreground font-normal'>Bet Type</h5>
                {betTypesParsed?.slice(0, betTypesParsed.length - 1) ?? '-'}
            </div>
            <div>
                <h5 className='m-0 text-xs text-muted-foreground font-normal'>Min Bet Odds</h5>
                <span>{minBetsOddsParsed ?? '-'}</span>
            </div>
        </div>
    );
});

const SchedulingCasino = observer('SchedulingCasino', () => {
    return (
        <>
            <h4 className='text-sm m-0 mb-2 font-semibold'>Scheduling</h4>
            <div className='mb-4 flex gap-2'>
                <div className='w-1/2'>
                    <h5 className='m-0 text-xs text-muted-foreground font-normal'>Instalment Count</h5>
                    <span className='m-0 text-sm'>-</span>
                </div>
                <div className='w-1/2'>
                    <h5 className='m-0 text-xs text-muted-foreground font-normal'>Reward Interval</h5>
                    <span className='m-0 text-sm'>-</span>
                </div>
            </div>
        </>
    );
});

const StakeCriteriaSportsbook = observer(
    'StakeCriteriaSportsbook',
    ({ campaignModel: { actionConditions } }: ActionCondiionsPropsType) => {
        if (actionConditions?.bettingPlatform !== 'sports-book') {
            return null;
        }

        const targetStake = checkIsNumber(
            actionConditions.criteria?.find((item) => item.type === 'target-stake')?.value
        );
        const stakeRequirements = targetStake === null ? 'Single' : 'Cumulative';
        const minStake = checkIsNumber(actionConditions.criteria?.find((item) => item.type === 'min-stake')?.value);
        const minBetFrequency = checkIsNumber(
            actionConditions.criteria?.find((item) => item.type === 'min-bet-frequency')?.value
        );

        return (
            <div className='flex flex-col'>
                {/* <h4 className='text-sm m-0 mb-2 font-semibold'>Scheduling</h4>
                <div className='mb-4 flex gap-2'>
                    <div className='w-1/2'>
                        <h5 className='m-0 text-xs text-muted-foreground font-normal'>Instalment Count</h5>
                        <span className='m-0 text-sm'>-</span>
                    </div>
                    <div className='w-1/2'>
                        <h5 className='m-0 text-xs text-muted-foreground font-normal'>Reward Interval</h5>
                        <span className='m-0 text-sm'>-</span>
                    </div>
                </div> */}
                <h4 className='text-sm m-0 mb-2 font-semibold'>Stake Criteria</h4>
                <div className='mb-3 flex gap-2'>
                    <div className='w-1/2'>
                        <h5 className='m-0 text-xs text-muted-foreground font-normal'>Stake Requirements</h5>
                        <span className='m-0 text-sm'>{stakeRequirements}</span>
                    </div>
                    <div className='w-1/2'>
                        <h5 className='m-0 text-xs text-muted-foreground font-normal'>Target Stake</h5>
                        <span className='m-0 text-sm'>{targetStake ?? '-'}</span>
                    </div>
                </div>
                <div className='mb-3 flex gap-2'>
                    <div className='w-1/2'>
                        <h5 className='m-0 text-xs text-muted-foreground font-normal'>Min Stake</h5>
                        <span className='m-0 text-sm'>{minStake ?? '-'}</span>
                    </div>
                    <div className='w-1/2'>
                        <h5 className='m-0 text-xs text-muted-foreground font-normal'>Min Bet Frequency</h5>
                        <span className='m-0 text-sm'>{minBetFrequency ?? '-'}</span>
                    </div>
                </div>
            </div>
        );
    }
);

const StakeCriteriaCasino = observer(
    'StakeCriteriaCasino',
    ({ campaignModel: { actionConditions } }: ActionCondiionsPropsType) => {
        if (actionConditions?.bettingPlatform !== 'casino') {
            return null;
        }

        const result = checkString(actionConditions.criteria?.find((item) => item.type === 'casino-result')?.value);
        const minSpins = checkIsNumber(actionConditions.criteria?.find((item) => item.type === 'min-spins')?.value);
        const stakeType = minSpins === null ? 'Monetary Value' : 'Minimum Rounds';
        const minMonetaryValue = checkIsNumber(
            actionConditions.criteria?.find((item) => item.type === 'min-monetary-value')?.value
        );

        return (
            <div className='flex flex-col'>
                <h4 className='text-sm m-0 mb-2 font-semibold'>Stake Criteria</h4>
                <div className='mb-3 flex gap-2'>
                    <div className='w-1/2'>
                        <h5 className='m-0 text-xs text-muted-foreground font-normal'>Result</h5>
                        <span className='m-0 text-sm'>{result ?? '-'}</span>
                    </div>
                </div>
                <div className='mb-3 flex gap-2'>
                    <div className='w-1/2'>
                        <h5 className='m-0 text-xs text-muted-foreground font-normal'>Stake Type</h5>
                        <span className='m-0 text-sm'>{stakeType}</span>
                    </div>
                    <div className='w-1/2'>
                        <h5 className='m-0 text-xs text-muted-foreground font-normal'>Value</h5>
                        <span className='m-0 text-sm'>
                            {stakeType === 'Minimum Rounds' ? minSpins : minMonetaryValue?.toFixed(2)}
                        </span>
                    </div>
                </div>
            </div>
        );
    }
);

const BettingPlatform = {
    'sports-book': 'Sports Book',
    casino: 'Casino',
};

export const ActionConditions = observer('ActionCondiions', ({ campaignModel }: ActionCondiionsPropsType) => {
    const bettingPlatform = campaignModel.actionConditions?.bettingPlatform ?? null;
    if (bettingPlatform === null) {
        return null;
    }

    return (
        <InfoBox>
            <h3 className='m-0 mb-4 text-base font-semibold'>Action Conditions</h3>
            <div className='flex gap-8 mb-4'>
                <div className='w-1/2'>
                    <div className='flex flex-col'>
                        <h4 className='text-sm m-0 mb-2 font-semibold'>Betting Platform</h4>
                        <div className='mb-4'>
                            <h5 className='m-0 text-xs text-muted-foreground font-normal'>Betting Platform</h5>
                            <span className='text-sm'>
                                {campaignModel.actionConditions?.bettingPlatform === null
                                    ? '-'
                                    : BettingPlatform[bettingPlatform]}
                            </span>
                        </div>
                        {bettingPlatform === 'casino' ? (
                            <SchedulingCasino />
                        ) : (
                            <BettingType campaignModel={campaignModel} />
                        )}
                    </div>
                </div>
                <div className='w-1/2'>
                    {bettingPlatform === 'casino' ? (
                        <StakeCriteriaCasino campaignModel={campaignModel} />
                    ) : (
                        <StakeCriteriaSportsbook campaignModel={campaignModel} />
                    )}
                </div>
            </div>
            {bettingPlatform === 'casino' ? (
                <CasinoGamesViewer
                    campaignModel={campaignModel}
                    type='action-conditions'
                />
            ) : (
                <SportsViewer
                    campaignModel={campaignModel}
                    type='action-conditions'
                />
            )}
        </InfoBox>
    );
});
